import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { subscribeToWeeklyQuote } from "./api";
import { trackNewsletterSignup } from "../../utils/analytics";

// A Rory quote and a bias a week by email. Email and first name, the same
// Captcha as the newsletter form, plus a hidden field only a bot would fill
// in. On a course page it speaks to someone not ready to enrol yet.
const SignupBox = ({ variant = "finder" }: { variant?: "finder" | "course" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "sending") return;
    setError(null);
    if (!firstName.trim()) {
      setError("What should we call you?");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("That email address doesn\u2019t look right.");
      return;
    }
    if (!captcha) {
      setError("Please tick the Captcha box.");
      return;
    }
    setState("sending");
    try {
      const accepted = await subscribeToWeeklyQuote(email.trim(), firstName.trim(), honeypot, captcha);
      // Only a sign-up the server really accepted is counted. It answers a
      // bot that filled the hidden field with an empty "OK", so the thanks
      // still show, but there is no subscriber and nothing to count.
      if (accepted?.email) trackNewsletterSignup({ list: "rory_weekly" });
      setState("done");
    } catch (err: any) {
      const data = err?.response?.data;
      const first = Array.isArray(data?.errors) ? data.errors[0] : null;
      setError(
        data?.captcha_error ||
          (first && (first.message || first.detail || (typeof first === "string" ? first : null))) ||
          data?.error ||
          "Something went wrong. Please try again.",
      );
      setState("idle");
    }
  };

  return (
    <section className="rq-signup" id="weekly-quote" aria-labelledby="rq-signup-title">
      <div className="rq-wrap">
        <div className="rq-signup__box rq-offset">
          <img className="rq-signup__sketch" src="/rory-quotes/rory-sketch.png" alt="" width="120" height="120" loading="lazy" />
          <div className="rq-signup__copy">
            <span className="rq-eyebrow rq-eyebrow--blue">{variant === "course" ? "Not ready to enrol yet?" : "Free, every Monday"}</span>
            <h2 className="rq-h2" id="rq-signup-title">
              {variant === "course"
                ? "A Rory Sutherland quote and one behavioural bias, free, every Monday."
                : "A Rory quote and one behavioural bias, every Monday."}
            </h2>
            <p>
              {variant === "course"
                ? "Start small. One verified Rory line and one behavioural science idea explained in a couple of sentences, with a real example, in your inbox each Monday. No cost, and one click stops it."
                : "A verified line to open a meeting or a deck with, plus one behavioural science idea explained in a couple of sentences, with a real example. Sent by us, and one click stops it."}
            </p>
          </div>
          {state === "done" ? (
            <p className="rq-signup__done" role="status">
              Done. Your first quote and bias are on their way, and the next pair lands on Monday. Until then, there are{" "}
              <a href="/rory-quotes/top">the top ten</a>.
            </p>
          ) : (
            <form className="rq-signup__form" onSubmit={submit} noValidate>
              <label htmlFor="rq-signup-name">First name</label>
              <input
                id="rq-signup-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Rory"
                maxLength={50}
              />
              <label htmlFor="rq-signup-email">Email</label>
              <input
                id="rq-signup-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
              <div className="rq-signup__trap" aria-hidden="true">
                <label htmlFor="rq-signup-username">Username</label>
                <input id="rq-signup-username" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              <div className="rq-signup__captcha">
                <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_TOKEN} onChange={(token) => setCaptcha(token || "")} />
              </div>
              <button type="submit" className="rq-btn rq-btn--blue" disabled={state === "sending"}>
                {state === "sending" ? "Signing you up\u2026" : "Send me the Monday email"}
              </button>
              {error && (
                <p className="rq-finder__error" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default SignupBox;
